import { Link, useLocation, useNavigate } from "react-router-dom";
import BrandLogo from "@/components/BrandLogo";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Settings, ArrowRightLeft } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRole } from "@/hooks/useUserRole";
import {
  User,
  Home,
  FlaskConical,
  HelpCircle,
  DollarSign,
  LayoutDashboard,
  Calendar,
  ShieldCheck,
  BarChart3,
  LogIn,
  Rocket,
  LogOut,
  Stethoscope,
  Info,
  Briefcase,
  Mail,
  FileText,
  Lock,
  Building2,
  Shield,
  Pill,
  TrendingUp,
} from "lucide-react";

interface MobileNavDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface NavItem {
  to: string;
  label: string;
  icon: React.ElementType;
  isHash?: boolean;
}

const MobileNavDrawer = ({ open, onOpenChange }: MobileNavDrawerProps) => {
  const { user, signOut, loading: authLoading } = useAuth();
  const { isAdmin, isProvider, isSalesperson, isPharmacy, loading: roleLoading } = useUserRole();
  const location = useLocation();
  const navigate = useNavigate();
  const rolesReady = !authLoading && !roleLoading;

  const close = () => onOpenChange(false);

  const isActive = (path: string) => location.pathname === path;

  const publicNav: NavItem[] = user
    ? [
        { to: "/", label: "Home", icon: Home },
        { to: "/peptides", label: "Therapies", icon: FlaskConical },
      ]
    : [
        { to: "/", label: "Home", icon: Home },
        { to: "/peptides", label: "Therapies", icon: FlaskConical },
        { to: "/#how-it-works", label: "How It Works", icon: HelpCircle, isHash: true },
        { to: "/#pricing", label: "Pricing", icon: DollarSign, isHash: true },
      ];

  const partnerNav: NavItem[] = [
    { to: "/join/provider", label: "For Providers", icon: Stethoscope },
    { to: "/join/sales", label: "For Sales Partners", icon: TrendingUp },
    { to: "/join/pharmacy", label: "For Pharmacies", icon: Pill },
  ];

  const patientNav: NavItem[] = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/book", label: "Book Appointment", icon: Calendar },
    { to: "/settings", label: "Settings", icon: Settings },
  ];

  const roleViews = [
    { to: "/admin", label: "Admin", icon: Shield, show: rolesReady && isAdmin },
    { to: "/provider", label: "Provider", icon: Stethoscope, show: rolesReady && (isProvider || isAdmin) },
    { to: "/sales", label: "Sales", icon: TrendingUp, show: rolesReady && (isSalesperson || isAdmin) },
    { to: "/pharmacy", label: "Pharmacy", icon: Building2, show: rolesReady && (isPharmacy || isAdmin) },
  ].filter((v) => v.show);

  const footerNav: NavItem[] = [
    { to: "/about", label: "About", icon: Info },
    { to: "/careers", label: "Careers", icon: Briefcase },
    { to: "/contact", label: "Contact", icon: Mail },
    { to: "/privacy", label: "Privacy", icon: FileText },
    { to: "/terms", label: "Terms", icon: FileText },
    { to: "/hipaa", label: "HIPAA Notice", icon: Lock },
  ];

  const renderSection = (title: string, items: NavItem[]) => (
    <div className="space-y-1">
      <p className="px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground/60 mb-2">
        {title}
      </p>
      {items.map((item) =>
        item.isHash ? (
          <button
            key={item.to}
            onClick={() => {
              close();
              const hash = item.to.replace("/#", "");
              const scrollTo = () => {
                const el = document.getElementById(hash);
                if (el) el.scrollIntoView({ behavior: "smooth" });
              };
              if (location.pathname !== "/") {
                navigate("/#" + hash);
                setTimeout(scrollTo, 150);
              } else {
                scrollTo();
              }
            }}
            className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground w-full text-left"
          >
            <item.icon className="h-4 w-4" />
            {item.label}
          </button>
        ) : (
          <Link
            key={item.to}
            to={item.to}
            onClick={close}
            className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
              isActive(item.to)
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground hover:bg-accent hover:text-foreground"
            }`}
          >
            <item.icon className="h-4 w-4" />
            {item.label}
          </Link>
        )
      )}
    </div>
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="left" className="w-[280px] p-0 flex flex-col">
        <SheetHeader className="px-4 pt-5 pb-3">
          <SheetTitle className="text-left">
            <BrandLogo size="sm" onClick={close} />
          </SheetTitle>
          <SheetDescription className="sr-only">
            Navigation menu
          </SheetDescription>
        </SheetHeader>

        <Separator />

        <div className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
          {renderSection("Navigate", publicNav)}

          {!user && (
            <>
              <Separator />
              {renderSection("For Professionals", partnerNav)}
            </>
          )}

          {user && (
            <>
              <Separator />
              {renderSection("My Care", patientNav)}
            </>
          )}

          {roleViews.length > 0 && (
            <>
              <Separator />
              <div className="space-y-1">
                <p className="px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground/60 mb-2 flex items-center gap-1.5">
                  <ArrowRightLeft className="h-3 w-3" />
                  Switch View
                </p>
                <div className="grid grid-cols-2 gap-2 px-1">
                  {roleViews.map((view) => {
                    const active = location.pathname.startsWith(view.to);
                    return (
                      <Link
                        key={view.to}
                        to={view.to}
                        onClick={close}
                        className={`flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                          active
                            ? "bg-primary/10 text-primary border border-primary/20"
                            : "text-muted-foreground hover:bg-accent hover:text-foreground border border-transparent"
                        }`}
                      >
                        <view.icon className="h-4 w-4" />
                        {view.label}
                      </Link>
                    );
                  })}
                </div>
                {(isProvider || isAdmin) && (
                  <Link
                    to="/provider/profile"
                    onClick={close}
                    className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors mt-1 ${
                      isActive("/provider/profile")
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:bg-accent hover:text-foreground"
                    }`}
                  >
                    <User className="h-4 w-4" />
                    Provider Profile
                  </Link>
                )}
              </div>
            </>
          )}

          <Separator />
          {renderSection("Company", footerNav)}
        </div>

        <Separator />
        <div className="p-4">
          {user ? (
            <Button
              variant="outline"
              className="w-full justify-start gap-2"
              onClick={() => {
                signOut();
                close();
              }}
            >
              <LogOut className="h-4 w-4" />
              Sign Out
            </Button>
          ) : (
            <div className="flex flex-col gap-2">
              <Link to="/login" onClick={close}>
                <Button variant="outline" className="w-full justify-start gap-2">
                  <LogIn className="h-4 w-4" />
                  Log In
                </Button>
              </Link>
              <Link to="/get-started" onClick={close}>
                <Button className="w-full justify-start gap-2">
                  <Rocket className="h-4 w-4" />
                  Get Started
                </Button>
              </Link>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default MobileNavDrawer;
